import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../base/base.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ErrorReportingService } from '../services/errorReporting/error-reporting.service';
import { SchedulerService, ConfigSchedule, ScheduleDay, ScheduleFrequencyType, ScheduleRunStatusType, ConfigScheduleHistoryItem } from '../services/scheduler/scheduler.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NewScheduleDialogComponent } from './new-schedule-dialog/new-schedule-dialog.component';
import { Subscription, interval,Observable,BehaviorSubject } from 'rxjs';
import { DatePipe } from '@angular/common';

enum ScheduleStatus {
  running = 0,
  failed = 1,
  pending = 2
}

class ScheduleViewModel {
  id: number;
  scheduleName: string;
  scheduleDisplayString: string;
  lastRunDate?: Date;
  nextRunDate?: Date;
  status: ScheduleStatus;
  configSchedule: ConfigSchedule;

  constructor(schedule: ConfigSchedule, private datePipe: DatePipe) {
    this.id = schedule.id;
    this.configSchedule = schedule;
    this.scheduleName = schedule.scheduleName;
    this.lastRunDate = null;
    this.status = schedule.isRunning ? ScheduleStatus.running : ScheduleStatus.pending;
    this.scheduleDisplayString = this.createDisplayString();
  }

  private createDisplayString(): string {
    const startDate = this.datePipe.transform(this.configSchedule.activeStartDate, 'MM/dd/yyyy');
    switch (this.configSchedule.frequencyType) {
      case ScheduleFrequencyType.Daily:
        return `Occurs every day at ${this.getTimeDisplay(this.configSchedule.activeStartTime)}` +
        ` starting on ${startDate}`;
      case ScheduleFrequencyType.Weekly:
        return `Occurs every week (${this.getDays()}) at ${this.getTimeDisplay(this.configSchedule.activeStartTime)}` +
        ` starting on ${startDate}`;
      case ScheduleFrequencyType.Monthly:
        return `Occurs every month (${this.getDays()}) at ${this.getTimeDisplay(this.configSchedule.activeStartTime)}` +
        ` starting on ${startDate}`;
    }
    return '';
  }

  private getTimeDisplay(sqlTime: number): string {
    const hr24 = Math.floor((sqlTime / 10000));
    const hr = hr24 > 12 ? hr24 - 12 : hr24;
    // (sqlTime % 100); // seconds
    const min = ((sqlTime % 10000) / 100);
    const minSub10 = `0${min}`;
    let timeDisplay = `${hr}:${min > 9 ? min : minSub10}`;
    if (hr24 >= 12) {
      timeDisplay += ' PM';
    } else {
      timeDisplay += ' AM';
    }

    return timeDisplay;
  }

  private getDays(): string {
    const days = [];
    // tslint:disable-next-line:no-bitwise
    if ((this.configSchedule.days & ScheduleDay.Sunday) === ScheduleDay.Sunday) {
      days.push('Sun');
    }
    // tslint:disable-next-line:no-bitwise
    if ((this.configSchedule.days & ScheduleDay.Monday) === ScheduleDay.Monday) {
      days.push('Mon');
    }
    // tslint:disable-next-line:no-bitwise
    if ((this.configSchedule.days & ScheduleDay.Tuesday) === ScheduleDay.Tuesday) {
      days.push('Tues');
    }
    // tslint:disable-next-line:no-bitwise
    if ((this.configSchedule.days & ScheduleDay.Wednesday) === ScheduleDay.Wednesday) {
      days.push('Wed');
    }
    // tslint:disable-next-line:no-bitwise
    if ((this.configSchedule.days & ScheduleDay.Thursday) === ScheduleDay.Thursday) {
      days.push('Thurs');
    }
    // tslint:disable-next-line:no-bitwise
    if ((this.configSchedule.days & ScheduleDay.Friday) === ScheduleDay.Friday) {
      days.push('Fri');
    }
    // tslint:disable-next-line:no-bitwise
    if ((this.configSchedule.days & ScheduleDay.Saturday) === ScheduleDay.Saturday) {
      days.push('Sat');
    }

    return days.join(', ');
  }
}

@Component({
    templateUrl: './scheduler.component.html',
    styleUrls: ['./scheduler.component.css'],
    standalone: false
})
export class SchedulerComponent extends BaseComponent implements OnInit, OnDestroy {
  isLinear = false;
  scheduleStatusType = ScheduleStatus;
  scheduleRunStatusType = ScheduleRunStatusType;
  schedules = new BehaviorSubject<ScheduleViewModel[]>([]);
  historyItems = new BehaviorSubject<ConfigScheduleHistoryItem[]>([]);
  dataSource: Observable<ScheduleViewModel[]> | undefined;
  displayedColumns: string[] = ['status', 'name', 'schedule', 'lastRunDate', 'actions'];
  displayedColumnsHistory: string[] = ['status', 'historyId',  'scheduleName', 'startTime', 'endTime'];
  hasSchedules = false;
  hasScheduleHistory = false;
  firstFormGroup: UntypedFormGroup;
  secondFormGroup: UntypedFormGroup;
  timerSubscription: Subscription;
  private destroyed = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    errorLogger: ErrorReportingService,
    matDialog: MatDialog,
    private schedulerService: SchedulerService,
    private snackBar: MatSnackBar,
    public datePipe: DatePipe) {
    super(errorLogger, matDialog);
  }

  async ngOnInit(): Promise<void> {
    this.firstFormGroup = this.formBuilder.group({
      firstCtrl: ['', Validators.required]
    });
    this.secondFormGroup = this.formBuilder.group({
      secondCtrl: ['', Validators.required]
    });

    await this.loadSchedules();
    await this.loadAllHistory();

    // The component may have been destroyed while the initial loads were awaited;
    // don't start a poller that ngOnDestroy has already run past.
    if (this.destroyed) {
      return;
    }
    this.timerSubscription = interval(2500).subscribe((x) => {
      this.loadSchedules();
      this.loadAllHistory();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.timerSubscription?.unsubscribe();
  }

  private async loadSchedules(): Promise<void> {
    try {
      const schedules = (await this.schedulerService.getAllConfigSchedules()).map( (schedule) => {
        return new ScheduleViewModel(schedule, this.datePipe);
      });

      //console.log(schedules);
      this.schedules.next(schedules);
      this.hasSchedules = schedules.length > 0;
    } catch (err) {
      this.logError('Failed to get list of schedules', err);
      // this.snackBar.open('Failed to get list of schedules', 'Dismiss', {
      //   duration: 2500
      // });
    }
  }

  private async loadAllHistory(): Promise<void> {
    try {
      const history = (await this.schedulerService.getAllConfigScheduleHistoryItems()).map( (item) => {
        return item;
      });

      //console.log(history);
      this.historyItems.next(history.sort((a, b) => a.historyId > b.historyId ? 1 : -1));
      this.hasScheduleHistory = history.length > 0;
    } catch (err) {
      this.logError('Failed to get list of history items', err);
      // this.snackBar.open('Failed to get list of history items', 'Dismiss', {
      //   duration: 2500
      // });
    }
  }

  async editSchedule(schedule: ScheduleViewModel): Promise<void> {
    // Load the AddNewSchedule Form but in edit mode
    this.showEditConfigScheduleDialog(schedule.configSchedule, async (configSchedule: ConfigSchedule) => {
      try {
        //console.log('existing schedule has been created from UI...sending to server...');
        const newSchedule = await this.schedulerService.upsertConfigSchedule(configSchedule);
        this.schedules.value.push(new ScheduleViewModel(newSchedule, this.datePipe));
        this.schedules.next(this.schedules.value);
        this.hasSchedules = this.schedules.value.length > 0;
        //console.log(newSchedule);
        this.snackBar.open(`Successfully added new schedule '${newSchedule.scheduleName}'`, 'Dismiss', {
          duration: 5000
        });
      } catch (err) {
        this.displayError('Failed to edit schedule', 'Error', err);
      }
    });
  }

  async deleteSchedule(scheduleId: number): Promise<void> {

    this.showConfirmDialog('Delete Schedule', `Are you sure you want to delete the schedule?`, async (confirmed) => {
      if (confirmed) {
        try {
          const result = await this.schedulerService.deleteConfigSchedule(scheduleId);
          if (!result.deleted) {
            this.displayError(result.errorMessage, 'Failed to Delete');
          } else {
            this.loadSchedules();
            this.snackBar.open('Successfully deleted schedule', 'Dismiss', {
              duration: 5000
            });
          }
        } catch (err) {
          this.displayError('Failed to delete schedule', 'Error', err);
        }
      }
    });
  }

  async addNewSchedule(): Promise<void> {

    this.showNewConfigScheduleDialog(async (schedule: ConfigSchedule) => {
      try {
        //console.log('new schedule has been created from UI...sending to server...');
        const newSchedule = await this.schedulerService.upsertConfigSchedule(schedule);
        this.schedules.value.push(new ScheduleViewModel(newSchedule, this.datePipe));
        this.schedules.next(this.schedules.value);
        this.hasSchedules = this.schedules.value.length > 0;
        //console.log(newSchedule);
        this.snackBar.open(`Successfully added new schedule '${newSchedule.scheduleName}'`, 'Dismiss', {
          duration: 5000
        });
      } catch (err) {
        this.displayError('Failed to add new schedule', 'Error', err);
      }
    });
  }

  showEditConfigScheduleDialog(scheduleToEdit: ConfigSchedule, onClosed: (schedule: ConfigSchedule) => any ): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '400px';
    dialogConfig.data = {schedule: scheduleToEdit};

    const dialogRef = this.matDialog.open(NewScheduleDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result.newSchedule) { return; }

      // Schedule object is available
      onClosed(result.newSchedule);
    });
  }

  showNewConfigScheduleDialog(onClosed: (schedule: ConfigSchedule) => any ): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '400px';

    const dialogRef = this.matDialog.open(NewScheduleDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result.newSchedule) { return; }

      // Schedule object is available
      onClosed(result.newSchedule);
    });
  }
}
